import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { BottomNavProps, BottomNavItem } from './types';
import { matchActivePath } from './active-path';

// ─── Constants ──────────────────────────────────────────────────

/** Max visible nav items (including "More"). Spec: 5 items max. */
const MAX_VISIBLE_ITEMS = 4;

/** Swipe distance (px) required to dismiss the drawer */
const SWIPE_DISMISS_THRESHOLD = 80;

// ─── Badge component ────────────────────────────────────────────

function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className={[
        'absolute -right-1.5 -top-1 flex h-[18px] min-w-[18px] items-center',
        'justify-center rounded-full bg-danger-500 px-1',
        'text-[10px] font-bold leading-none text-white',
        'animate-checkmark',
      ].join(' ')}
      aria-hidden="true"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// ─── Lock mark (item not in the business's plan) ────────────────

function LockMark({ className }: { className: string }) {
  return (
    <span
      role="img"
      aria-label="Not in your plan"
      title="Not in your plan"
      className={className}
      data-bottom-nav-lock="true"
    >
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    </span>
  );
}

// ─── Single nav item ────────────────────────────────────────────

interface NavItemProps {
  item: BottomNavItem;
  isActive: boolean;
  isFocused: boolean;
  onNavigate?: (path: string) => void;
  onHaptic?: () => void;
  itemRef: (el: HTMLButtonElement | null) => void;
}

function NavItem({
  item,
  isActive,
  isFocused,
  onNavigate,
  onHaptic,
  itemRef,
}: NavItemProps) {
  const handleClick = useCallback(() => {
    onHaptic?.();
    onNavigate?.(item.path);
  }, [onHaptic, onNavigate, item.path]);

  return (
    <button
      ref={itemRef}
      type="button"
      onClick={handleClick}
      tabIndex={isFocused ? 0 : -1}
      className={[
        'flex flex-1 flex-col items-center justify-center gap-0.5',
        'min-h-[48px] min-w-[48px]',
        'border-t-[3px] transition-colors duration-150',
        isActive
          ? 'border-brand-500 bg-brand-50 text-brand-500 dark:bg-brand-900/20'
          : item.locked
            // Not in the plan: muted, but still a link — the route explains the lock.
            ? 'border-transparent text-neutral-400 active:bg-brand-100 dark:text-neutral-600 dark:active:bg-brand-900/10'
            : 'border-transparent text-neutral-500 active:bg-brand-100 dark:text-neutral-400 dark:active:bg-brand-900/10',
      ].join(' ')}
      aria-current={isActive ? 'page' : undefined}
      aria-label={
        (item.badge && item.badge > 0
          ? `${item.label}, ${item.badge} notification${item.badge !== 1 ? 's' : ''}`
          : item.label) + (item.locked ? ', not in your plan' : '')
      }
    >
      <span className="relative text-lg" aria-hidden="true">
        {item.icon}
        <NavBadge count={item.badge ?? 0} />
        {item.locked && (
          <LockMark className="absolute -bottom-1 -right-2 rounded-full bg-white text-warning-600 dark:bg-neutral-900 dark:text-warning-400" />
        )}
      </span>
      <span className="text-[10px] font-medium leading-none">{item.label}</span>
    </button>
  );
}

// ─── "More" drawer item ─────────────────────────────────────────

interface DrawerItemProps {
  item: BottomNavItem;
  isActive: boolean;
  isFocused: boolean;
  onClick: () => void;
  itemRef: (el: HTMLButtonElement | null) => void;
}

function DrawerItem({ item, isActive, isFocused, onClick, itemRef }: DrawerItemProps) {
  return (
    <li>
      <button
        ref={itemRef}
        type="button"
        onClick={onClick}
        tabIndex={isFocused ? 0 : -1}
        role="menuitem"
        className={[
          'flex w-full items-center gap-3 rounded-lg px-4',
          'min-h-[56px] text-sm transition-colors duration-150',
          isActive
            ? 'bg-brand-50 font-medium text-brand-600 dark:bg-brand-900/20 dark:text-brand-400'
            : item.locked
              ? 'text-neutral-400 active:bg-neutral-100 dark:text-neutral-500 dark:active:bg-neutral-700'
              : 'text-neutral-700 active:bg-neutral-100 dark:text-neutral-300 dark:active:bg-neutral-700',
          isFocused && !isActive ? 'bg-neutral-50 dark:bg-neutral-700' : '',
        ].join(' ')}
      >
        <span
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center text-lg"
          aria-hidden="true"
        >
          {item.icon}
        </span>
        <span className="flex-1">{item.label}</span>
        {item.locked && (
          <LockMark className="flex-shrink-0 text-warning-500 dark:text-warning-400" />
        )}
        {item.badge !== undefined && item.badge > 0 && (
          <span
            className={[
              'flex h-5 min-w-5 items-center justify-center rounded-full px-1',
              'bg-danger-100 text-[10px] font-bold text-danger-700',
              'dark:bg-danger-900/30 dark:text-danger-400',
            ].join(' ')}
          >
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </button>
    </li>
  );
}

// ─── Swipe-to-dismiss hook ──────────────────────────────────────

function useSwipeDismiss(
  ref: React.RefObject<HTMLDivElement | null>,
  onDismiss: () => void,
  enabled: boolean,
) {
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  const startTime = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    function handleTouchStart(e: TouchEvent) {
      startY.current = e.touches[0].clientY;
      currentY.current = startY.current;
      startTime.current = Date.now();
      isDragging.current = false;
    }

    function handleTouchMove(e: TouchEvent) {
      currentY.current = e.touches[0].clientY;
      const deltaY = currentY.current - startY.current;

      // Only drag downward
      if (deltaY > 10) {
        isDragging.current = true;
        el!.style.transform = `translateY(${deltaY}px)`;
        el!.style.transition = 'none';
      }
    }

    function handleTouchEnd() {
      if (!isDragging.current) return;

      const deltaY = currentY.current - startY.current;
      const elapsed = Date.now() - startTime.current;
      const velocity = deltaY / Math.max(elapsed, 1);

      el!.style.transition = '';
      el!.style.transform = '';

      // Dismiss if dragged far enough or fast enough
      if (deltaY > SWIPE_DISMISS_THRESHOLD || velocity > 0.5) {
        onDismiss();
      }

      isDragging.current = false;
    }

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [ref, onDismiss, enabled]);
}

// ─── Main BottomNav Component ───────────────────────────────────

export function BottomNav({
  items,
  activeItem,
  onNavigate,
  moreItems,
  onHaptic,
}: BottomNavProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [navFocusedIndex, setNavFocusedIndex] = useState(0);
  const [drawerFocusedIndex, setDrawerFocusedIndex] = useState(0);

  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const drawerItemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // DEV: warn if items exceed MAX_VISIBLE_ITEMS when moreItems is present
  // Items beyond MAX_VISIBLE_ITEMS are automatically moved to the drawer.
  const visibleItems = useMemo(() => {
    if (items.length <= MAX_VISIBLE_ITEMS) return items;
    return items.slice(0, MAX_VISIBLE_ITEMS);
  }, [items]);

  const allMoreItems = useMemo(() => {
    const overflow = items.length > MAX_VISIBLE_ITEMS
      ? items.slice(MAX_VISIBLE_ITEMS)
      : [];
    return [...overflow, ...(moreItems ?? [])];
  }, [items, moreItems]);

  const hasMore = allMoreItems.length > 0;

  // The item the current path belongs to (/pos/quotations/q-1 → Quotes).
  const activeMatch = useMemo(
    () => matchActivePath([...visibleItems, ...allMoreItems].map((item) => item.path), activeItem),
    [visibleItems, allMoreItems, activeItem],
  );

  // Total focusable count in nav bar (visible items + more button)
  const totalNavItems = visibleItems.length + (hasMore ? 1 : 0);

  // ─── Drawer open/close ──────────────────────────────────────

  const openDrawer = useCallback(() => {
    onHaptic?.();
    setMoreOpen(true);
  }, [onHaptic]);

  const closeDrawer = useCallback(() => {
    setMoreOpen(false);
    // Return focus to "More" button
    requestAnimationFrame(() => {
      moreButtonRef.current?.focus();
    });
  }, []);

  // Swipe-to-dismiss
  useSwipeDismiss(drawerRef, closeDrawer, moreOpen);

  // ─── Drawer focus trap ────────────────────────────────────────

  useEffect(() => {
    if (!moreOpen) return;

    // Focus first drawer item on open
    setDrawerFocusedIndex(0);
    requestAnimationFrame(() => {
      drawerItemRefs.current[0]?.focus();
    });

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }

      // Tab trap within drawer
      if (e.key === 'Tab') {
        const drawer = drawerRef.current;
        if (!drawer) return;

        const focusable = drawer.querySelectorAll<HTMLElement>(
          'button, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [moreOpen, closeDrawer]);

  // ─── Nav bar keyboard navigation ─────────────────────────────

  const handleNavKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight': {
          e.preventDefault();
          const next = Math.min(navFocusedIndex + 1, totalNavItems - 1);
          setNavFocusedIndex(next);
          if (next < visibleItems.length) {
            navItemRefs.current[next]?.focus();
          } else {
            moreButtonRef.current?.focus();
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          const prev = Math.max(navFocusedIndex - 1, 0);
          setNavFocusedIndex(prev);
          if (prev < visibleItems.length) {
            navItemRefs.current[prev]?.focus();
          } else {
            moreButtonRef.current?.focus();
          }
          break;
        }
        case 'Home': {
          e.preventDefault();
          setNavFocusedIndex(0);
          navItemRefs.current[0]?.focus();
          break;
        }
        case 'End': {
          e.preventDefault();
          const last = totalNavItems - 1;
          setNavFocusedIndex(last);
          if (last < visibleItems.length) {
            navItemRefs.current[last]?.focus();
          } else {
            moreButtonRef.current?.focus();
          }
          break;
        }
      }
    },
    [navFocusedIndex, totalNavItems, visibleItems.length],
  );

  // ─── Drawer keyboard navigation ──────────────────────────────

  const handleDrawerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = Math.min(drawerFocusedIndex + 1, allMoreItems.length - 1);
          setDrawerFocusedIndex(next);
          drawerItemRefs.current[next]?.focus();
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = Math.max(drawerFocusedIndex - 1, 0);
          setDrawerFocusedIndex(prev);
          drawerItemRefs.current[prev]?.focus();
          break;
        }
        case 'Home': {
          e.preventDefault();
          setDrawerFocusedIndex(0);
          drawerItemRefs.current[0]?.focus();
          break;
        }
        case 'End': {
          e.preventDefault();
          const last = allMoreItems.length - 1;
          setDrawerFocusedIndex(last);
          drawerItemRefs.current[last]?.focus();
          break;
        }
      }
    },
    [drawerFocusedIndex, allMoreItems.length],
  );

  const handleDrawerItemClick = useCallback(
    (path: string) => {
      onHaptic?.();
      onNavigate?.(path);
      setMoreOpen(false);
    },
    [onHaptic, onNavigate],
  );

  return (
    <>
      {/* ─── Backdrop (always mounted, opacity transitions) ──── */}
      <div
        className={[
          'fixed inset-0 z-modal-backdrop bg-neutral-900/50',
          'transition-opacity duration-200',
          moreOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0',
        ].join(' ')}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* ─── "More" drawer (always mounted, translateY transitions) */}
      <div
        ref={drawerRef}
        className={[
          'fixed bottom-bottom-nav left-0 right-0 z-modal',
          'max-h-[60vh] overflow-y-auto',
          'rounded-t-xl border-t border-neutral-200 bg-white shadow-xl',
          'dark:border-neutral-700 dark:bg-neutral-800',
          'pb-[env(safe-area-inset-bottom)]',
          // Visibility is transitioned with the transform so the slide-out still plays. Closed,
          // translate-y-full leaves a nav-height strip on screen: it must be neither visible nor
          // hit-testable, or it swallows clicks on whatever sits at the bottom of the viewport.
          'transition-[transform,visibility] duration-200',
          moreOpen
            ? 'visible translate-y-0 ease-out'
            : 'invisible pointer-events-none translate-y-full ease-in',
        ].join(' ')}
        role="menu"
        aria-label="More navigation options"
        aria-hidden={!moreOpen}
        onKeyDown={handleDrawerKeyDown}
      >
        {/* Drawer header: drag handle + close button */}
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex-1" />
          {/* Drag handle */}
          <div className="h-1 w-8 rounded-full bg-neutral-300 dark:bg-neutral-600" />
          <div className="flex flex-1 justify-end">
            <button
              type="button"
              onClick={closeDrawer}
              className={[
                'flex h-8 w-8 items-center justify-center rounded-full',
                'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
                'dark:hover:bg-neutral-700 dark:hover:text-neutral-300',
                'transition-colors duration-150',
              ].join(' ')}
              aria-label="Close menu"
              tabIndex={moreOpen ? 0 : -1}
            >
              {/* TODO: replace with icon library */}
              <span className="text-sm" aria-hidden="true">✕</span>
            </button>
          </div>
        </div>

        {/* Drawer items */}
        <ul className="px-2 pb-4" role="none">
          {allMoreItems.map((item, i) => (
            <DrawerItem
              key={item.id}
              item={item}
              isActive={activeMatch === item.path}
              isFocused={moreOpen && drawerFocusedIndex === i}
              onClick={() => handleDrawerItemClick(item.path)}
              itemRef={(el) => { drawerItemRefs.current[i] = el; }}
            />
          ))}
        </ul>
      </div>

      {/* ─── Bottom nav bar ──────────────────────────────────── */}
      <nav
        aria-label="Bottom navigation"
        className={[
          'flex h-bottom-nav items-stretch',
          'border-t border-neutral-200 bg-white',
          'dark:border-neutral-700 dark:bg-neutral-900',
          'pb-[env(safe-area-inset-bottom)]',
        ].join(' ')}
        onKeyDown={handleNavKeyDown}
      >
        {visibleItems.map((item, i) => (
          <NavItem
            key={item.id}
            item={item}
            isActive={activeMatch === item.path}
            isFocused={navFocusedIndex === i}
            onNavigate={onNavigate}
            onHaptic={onHaptic}
            itemRef={(el) => { navItemRefs.current[i] = el; }}
          />
        ))}

        {/* "More" button */}
        {hasMore && (
          <button
            ref={moreButtonRef}
            type="button"
            onClick={moreOpen ? closeDrawer : openDrawer}
            tabIndex={navFocusedIndex === visibleItems.length ? 0 : -1}
            className={[
              'flex flex-1 flex-col items-center justify-center gap-0.5',
              'min-h-[48px] min-w-[48px]',
              'border-t-[3px] transition-colors duration-150',
              moreOpen
                ? 'border-brand-500 bg-brand-50 text-brand-500 dark:bg-brand-900/20'
                : 'border-transparent text-neutral-500 active:bg-brand-100 dark:text-neutral-400',
            ].join(' ')}
            aria-label="More options"
            aria-expanded={moreOpen}
            aria-haspopup="menu"
          >
            {/* TODO: replace with icon library */}
            <span className="text-lg" aria-hidden="true">⋯</span>
            <span className="text-[10px] font-medium leading-none">More</span>
          </button>
        )}
      </nav>
    </>
  );
}
